import React, { FC, useState } from 'react';
import Modal, { insideWrapperStyles } from '~ComponentsImproved/Modal';
import { Box, Button, TransparentButton } from '~Styles';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import styled from '@emotion/styled';
import Icon from '~ComponentsImproved/Icon';
import LicenseIcon from '~Assets/icons/license.svg';
import NavigateLeftIcon from '~/Assets/icons/navigate-left.svg';
import { useButton, useDispatch, useI18n, useLicenses, useLogger, useSelector } from '~Hooks';
import { useHistory } from 'react-router-dom';
import { BAEMINGO_PRODUCT } from '../../../../../__queries__';
import { useMutation, useQuery } from '@apollo/client';
import {
  GetActiveLicenses,
  UpdateActiveLicense,
} from '~Providers/LicensesProvider/api/__queries__';
import { UPDATE_ACTIVE_LICENSE } from '~Providers/LicensesProvider/api/mutations';
import { ACTIVE_LICENSES_QUERY } from '~Providers/LicensesProvider/api/queries';
import Loader from '~ComponentsImproved/Loader';
import { handyBreakpoint } from '~Styles/breakpoints';
import { useStorage } from '~Components/Storage';
import { useDeviceHandler } from '~Components/DeviceProvider';
import * as actions from '~actions';

const ClaimLicenseButton = styled(Button)`
  min-width: 160px;
`;

const LoadingWrapper = styled(Box)`
  height: 492px;
  min-width: 592px;
  ${handyBreakpoint} {
    height: auto;
    min-width: 100%;
  }
`;

const HandyWrapper = styled(Box)`
  width: 100vw;
  height: 100%;
  background: var(--colorBlueGray1200);
  position: relative;
`;

const ModalWrapper = styled(Box)`
  position: relative;
  box-shadow: ${insideWrapperStyles.boxShadow};
  border-radius: ${insideWrapperStyles.borderRadius};
  background-color: ${insideWrapperStyles.backgroundColor};
  animation: fadeIn 1s ease;
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const InfoBox = styled(Box)`
  background-color: var(--colorBlueGray1100);
  border-radius: 8px;
  padding: 16px;
`;

const BackButton = styled(TransparentButton)`
  position: absolute;
  top: 16px;
  left: 16px;
  color: var(--colorBlueGray800);
  font-size: 16px;
  text-transform: none;
`;

const BackButtonIcon = styled.div`
  width: 32px;
  height: 32px;
  mask-image: url(${NavigateLeftIcon});
  mask-size: contain;
  mask-repeat: no-repeat;
  background-color: var(--colorBlueGray800);
  margin-right: 8px;
`;

export const PRODUCT_TO_UI_COPY_MAP: Partial<Record<BAEMINGO_PRODUCT, string>> = {
  [BAEMINGO_PRODUCT.TSR]: 'TSR',
  [BAEMINGO_PRODUCT.QSR]: 'QSR',
  [BAEMINGO_PRODUCT.KDS]: 'KDS',
  [BAEMINGO_PRODUCT.EPOS]: 'EPOS',
  [BAEMINGO_PRODUCT.CUSTOMER_DISPLAY]: 'Customer display',
  [BAEMINGO_PRODUCT.HANDHELD_ORDER]: 'Handheld Order',
  [BAEMINGO_PRODUCT.HANDHELD_PRO]: 'Handheld Pro',
};

interface Props {
  product: BAEMINGO_PRODUCT;
  isHandy?: boolean;
}

/** Modal component that will be displayed if user has navigated to a product they don't have a license to */
const ClaimLicense: FC<Props> = ({ product, isHandy }) => {
  const { i18n } = useI18n();
  const logger = useLogger('claim-license');

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const licenses = useLicenses();
  const history = useHistory();
  const deviceHandler = useDeviceHandler();
  const dispatch = useDispatch();
  const [storage] = useStorage();

  const venueId = useSelector((state) => state.venueId);

  const [updateActiveLicense] = useMutation<UpdateActiveLicense>(UPDATE_ACTIVE_LICENSE);
  const venueLicensesQuery = useQuery<GetActiveLicenses>(ACTIVE_LICENSES_QUERY, {
    fetchPolicy: 'network-only',
  });

  const venueLicenses = venueLicensesQuery.data?.operativeViewer?.activeLicenses || [];
  const availableLicenses = venueLicenses.filter((license) => license.product === product);

  const handleActivateLicense = async () => {
    setLoading(true);

    /* Very old companies might not have a deviceID and therefore need to re-select it in settings to get one */
    if (!storage.deviceId) {
      setLoading(false);
      setError(i18n('ClaimLicense.Error.NoDeviceId'));
      logger.warn('ClaimLicense: No deviceId found in storage');
      return;
    }

    /* Check if we're using a device not assigned to this venue (to prevent browser signing in and claiming on other venue's devices) */
    if (!deviceHandler.activeDevice) {
      setLoading(false);
      setError(i18n('ClaimLicense.Error.WrongDevice'));
      logger.warn(
        'ClaimLicense: Device not assigned to this venue, deviceID: ' + storage.deviceId,
      );
      return;
    }

    logger.info(`Activating device license for product: ${product}`);

    try {
      await updateActiveLicense({
        variables: {
          id: availableLicenses[0].id,
          input: {
            deviceId: storage.deviceId,
          },
        },
      });

      licenses.refetch();
      dispatch(actions.addToast(`${product} has been activated for this device`, true));
    } catch (error) {
      logger.error('Failed to claim active license');
      if (typeof error === 'string') {
        setError(error);
      } else {
        setError(i18n('ClaimLicense.Error.General'));
      }
    }

    setLoading(false);
  };

  const backButton = useButton({
    onClick: () => {
      history.push(`/venue/${venueId}/gateway`);
    },
  });

  const claimLicenseButton = useButton({
    onClick: () => handleActivateLicense(),
  });

  const getContent = () => {
    if (venueLicensesQuery.loading) {
      return (
        <LoadingWrapper
          display="flex"
          alignItems="center"
          flexDirection="column"
          gap="small"
          justifyContent="center">
          <Loader color="var(--colorWhite)" />
          <Paragraph level={3} color="var(--colorWhite)">
            {i18n('ClaimLicense.Loading')}
          </Paragraph>
        </LoadingWrapper>
      );
    }

    return (
      <>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap="xs"
          justifyContent="center">
          <Icon
            height={isHandy ? '48px' : '64px'}
            width={isHandy ? '48px' : '64px'}
            src={LicenseIcon}
            color="var(--colorBlueGray200)"
          />

          <Heading level={isHandy ? 2 : 1} color="var(--colorWhite)">
            {i18n('ClaimLicense.LicenseRequired')}
          </Heading>
          <Paragraph
            style={{ textAlign: 'center' }}
            level={isHandy ? 4 : 2}
            color="var(--colorBlueGray200)">
            {availableLicenses.length > 0
              ? i18n('ClaimLicense.LicenseRequired.Claim')
              : i18n('ClaimLicense.LicenseRequired.NoAvailableLicenses')}
          </Paragraph>
        </Box>

        {availableLicenses.length > 0 && (
          <InfoBox fluid display="flex" alignItems="center" justifyContent="center">
            <Box display="flex" flexDirection="column" gap="xs" alignItems="center">
              <Paragraph level={5} color="var(--colorBlueGray200)">
                {i18n('ClaimLicense.AvailableLicenses', PRODUCT_TO_UI_COPY_MAP[product])}
              </Paragraph>
              <Heading level={5} color="var(--colorWhite)">
                {availableLicenses.length}
              </Heading>
            </Box>
          </InfoBox>
        )}

        <ClaimLicenseButton
          {...claimLicenseButton}
          size="large"
          disabled={availableLicenses.length === 0 || loading}>
          {loading ? <Loader color="var(--colorBlack)" size="sm" /> : i18n('ClaimLicense.Claim')}
        </ClaimLicenseButton>

        {error && (
          <Box display="flex" justifyContent="center">
            <Paragraph level={5} color="var(--colorUIRed)">
              {error}
            </Paragraph>
          </Box>
        )}
      </>
    );
  };

  /** If handy - don't display it in a modal */
  if (isHandy) {
    return (
      <HandyWrapper
        fluid
        display="flex"
        flexDirection="column"
        gap="small"
        alignItems="center"
        justifyContent="center"
        padding="small">
        {getContent()}
      </HandyWrapper>
    );
  }

  return (
    <Modal isShowing hide={() => {}} backdropClose={false}>
      <ModalWrapper
        display="flex"
        flexDirection="column"
        gap="medium"
        alignItems="center"
        justifyContent="center"
        padding="3xl"
        marginTop="xs"
        marginBottom="xs">
        <BackButton display="flex" alignItems="center" {...backButton}>
          <BackButtonIcon />
          {i18n('General.GoBack')}
        </BackButton>
        {getContent()}
      </ModalWrapper>
    </Modal>
  );
};

export default ClaimLicense;
